import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useCallback } from 'react';
import Loading from '../Loading/Loading';
import LandingPage from '../Landing/LandingPage';
import PrivacyPolicyPage from '../Landing/PrivacyPolicyPage';
import TermsOfServicePage from '../Landing/TermsOfServicePage';
import styles from './AuthenticatedApp.module.css';

const pathToPage = (path) => {
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    return 'landing';
};

/**
 * @fileoverview Authentication wrapper component with automatic stale token cleanup.
 *
 * This component wraps the main application and handles authentication state.
 * It includes automatic detection and cleanup of expired Auth0 tokens to prevent
 * "invalid state" errors when refresh tokens expire (30-day default lifetime).
 *
 * Features:
 * - Shows loading spinner while checking auth
 * - Shows login button if not authenticated
 * - Automatically clears expired tokens and forces re-login (with circuit breaker)
 * - Renders main app when authenticated
 * - Prevents infinite reload loops with retry limit
 *
 * @component
 */

// Circuit breaker constants
const MAX_AUTH_RETRIES = 3;
const RETRY_COUNT_KEY = 'auth_retry_count';
const RETRY_TIMESTAMP_KEY = 'auth_retry_timestamp';
const RETRY_RESET_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * AuthenticatedApp Component
 *
 * Wraps the main application and handles authentication state.
 * Shows loading spinner while checking auth, login button if not authenticated,
 * and the main app if authenticated.
 *
 * Automatically handles stale/expired auth tokens by:
 * 1. Detecting auth errors (invalid state, login required, etc.)
 * 2. Clearing Auth0 cache from localStorage
 * 3. Forcing page reload (with circuit breaker to prevent infinite loops)
 *
 * Circuit Breaker:
 * - Tracks retry attempts in localStorage
 * - Resets after 5 minutes of no errors
 * - Stops retrying after 3 attempts and shows permanent error message
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The main app component to render when authenticated
 * @returns {JSX.Element} Loading state, login button, error message, or authenticated app
 */
function AuthenticatedApp({ children }) {
    const { isLoading, isAuthenticated, error, logout } = useAuth0();
    const [permanentError, setPermanentError] = useState(null);
    // Public page routing for unauthenticated users — initialized from URL path
    const [publicPage, setPublicPage] = useState(() => pathToPage(window.location.pathname));

    // Navigate to a public page and update the browser URL
    const navigatePublic = useCallback((page) => {
        const path = page === 'privacy' ? '/privacy' : page === 'terms' ? '/terms' : '/';
        window.history.pushState(null, '', path);
        setPublicPage(page);
    }, []);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => setPublicPage(pathToPage(window.location.pathname));
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Handle stale/expired auth tokens with circuit breaker
    useEffect(() => {
        if (error && isAuthenticated === false) {
            console.error('[AUTH] Authentication error detected:', error.message);
            console.error('[AUTH] Full error object:', error);
            console.error('[AUTH] Current URL:', window.location.href);
            console.error('[AUTH] Environment check:', {
                domain: process.env.REACT_APP_AUTH0_DOMAIN,
                clientId: process.env.REACT_APP_AUTH0_CLIENT_ID?.substring(0, 10) + '...',
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI
            });

            // Check if it's an invalid state error (indicates stale tokens)
            const errorMessage = error.message?.toLowerCase() || '';
            const isInvalidStateError = errorMessage.includes('invalid state');

            if (isInvalidStateError) {
                // Circuit breaker: Check retry count
                const now = Date.now();
                const lastRetryTime = parseInt(localStorage.getItem(RETRY_TIMESTAMP_KEY) || '0', 10);
                const retryCount = parseInt(localStorage.getItem(RETRY_COUNT_KEY) || '0', 10);

                // Reset retry count if it's been more than 5 minutes since last retry
                if (now - lastRetryTime > RETRY_RESET_WINDOW_MS) {
                    console.log('[AUTH] Resetting retry counter (timeout expired)');
                    localStorage.setItem(RETRY_COUNT_KEY, '0');
                    localStorage.setItem(RETRY_TIMESTAMP_KEY, now.toString());
                }

                const currentRetryCount = parseInt(localStorage.getItem(RETRY_COUNT_KEY) || '0', 10);

                if (currentRetryCount >= MAX_AUTH_RETRIES) {
                    console.error('[AUTH] Max retry attempts reached. Stopping reload loop.');
                    setPermanentError(
                        'Authentication failed after multiple attempts. ' +
                        'This usually means there\'s a configuration issue. ' +
                        'Please contact support or try again later.'
                    );

                    // Clear Auth0 tokens but don't reload
                    const localStorageKeys = Object.keys(localStorage);
                    localStorageKeys.forEach(key => {
                        if (key.startsWith('@@auth0spajs@@')) {
                            localStorage.removeItem(key);
                        }
                    });

                    return;
                }

                // Increment retry count and clear tokens
                console.log(`[AUTH] Clearing stale Auth0 tokens (attempt ${currentRetryCount + 1}/${MAX_AUTH_RETRIES})`);
                localStorage.setItem(RETRY_COUNT_KEY, (currentRetryCount + 1).toString());
                localStorage.setItem(RETRY_TIMESTAMP_KEY, now.toString());

                // Clear Auth0 cache from localStorage
                const localStorageKeys = Object.keys(localStorage);
                localStorageKeys.forEach(key => {
                    if (key.startsWith('@@auth0spajs@@')) {
                        localStorage.removeItem(key);
                    }
                });

                // CRITICAL: Navigate to clean URL (without auth callback parameters)
                // This prevents re-processing the same failed callback on reload
                console.log('[AUTH] Redirecting to clean URL to break callback loop');
                window.location.href = window.location.origin;
            }
        }

        // If authentication succeeds, reset retry counter
        if (isAuthenticated) {
            localStorage.removeItem(RETRY_COUNT_KEY);
            localStorage.removeItem(RETRY_TIMESTAMP_KEY);
        }
    }, [error, isAuthenticated, logout]);

    if (isLoading) {
        return <Loading />;
    }

    // Show permanent error if circuit breaker has tripped
    if (permanentError) {
        return (
            <div className={styles.errorContainer}>
                <h1 className={styles.errorTitle}>Authentication Error</h1>
                <p className={styles.errorMessage}>{permanentError}</p>
                <div className={styles.errorDetails}>
                    <p>Possible causes:</p>
                    <ul>
                        <li>Environment variables not configured correctly</li>
                        <li>Auth0 API audience mismatch between frontend and backend</li>
                        <li>Network or connectivity issues</li>
                    </ul>
                    <p>Error details: {error?.message}</p>
                </div>
                <button
                    onClick={() => {
                        // Reset everything and try again
                        localStorage.removeItem(RETRY_COUNT_KEY);
                        localStorage.removeItem(RETRY_TIMESTAMP_KEY);
                        window.location.reload();
                    }}
                    className={styles.retryButton}
                >
                    Reset and Try Again
                </button>
                <button
                    onClick={() => {
                        // Force logout and clear everything
                        localStorage.clear();
                        logout({ logoutParams: { returnTo: window.location.origin } });
                    }}
                    className={styles.logoutButton}
                >
                    Clear Cache and Logout
                </button>
            </div>
        );
    }

    // Legal pages are accessible regardless of auth state
    if (publicPage === 'privacy') {
        return <PrivacyPolicyPage onBack={() => navigatePublic('landing')} />;
    }
    if (publicPage === 'terms') {
        return <TermsOfServicePage onBack={() => navigatePublic('landing')} />;
    }

    // Don't show errors during login flow - just show landing page
    // The useEffect above will handle clearing stale tokens if needed
    if (!isAuthenticated) {
        return (
            <LandingPage
                onNavigateToPrivacy={() => navigatePublic('privacy')}
                onNavigateToTerms={() => navigatePublic('terms')}
            />
        );
    }

    return children;
}

export default AuthenticatedApp;
